import React, { Fragment, useState } from "react";
import styled from "@emotion/styled";
import { Avatar, Box, Typography, IconButton, ListItemText, Tooltip, tooltipClasses, useMediaQuery, useTheme } from '@mui/material';
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { getAvatarUrl } from "../utils/avatarUtils";
import { addDays, differenceInDays, formatDate, formatDayLong, formatDayShort, toLocalDay } from "../utils/dateUtils";

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Styled Components
const Container = styled(Box)(({ theme }) => ({
    width: '100%',
    maxWidth: '100%',
    margin: '0 auto',
    padding: theme.spacing(2),
}));

const Header = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    gap: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
        gap: theme.spacing(0.5),
    },
}));

const WeekButton = styled(IconButton)(({ theme }) => ({
    padding: theme.spacing(0),
    minWidth: theme.spacing(4),
    minHeight: theme.spacing(4),
    fontSize: theme.typography.pxToRem(14),
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(0.5),
}));

const ButtonText = styled(Typography)(({ theme }) => ({
    [theme.breakpoints.down('sm')]: {
        display: 'none',
    },
}));

const DatesGrid = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: `repeat(15, minmax(0, 1fr))`,
    gap: theme.spacing(0),
    position: 'relative',
    marginBottom: theme.spacing(0),
    [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: `repeat(8, minmax(0, 1fr))`,
    },
    '&:before': {
        content: '""',
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
    }
}));

const ProjectBarsGrid = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: `repeat(15, minmax(0, 1fr))`,
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 0),
    position: 'relative',
    [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: `repeat(8, minmax(0, 1fr))`,
    },
    '&:before': {
        content: '""',
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
    }
}));

const DateBox = styled(Box, { shouldForwardProp: (prop) => prop !== 'isToday', })(({ theme, isToday }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(0.5, 1),
    backgroundColor: isToday ? theme.palette.primary.main : 'transparent',
    color: isToday ? theme.palette.primary.contrastText : theme.palette.text.primary,
    borderRadius: theme.shape.borderRadius*2,
    gap: 0,
}));

const DateText = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
}));

const DateDay = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightBold,
}));

const ProjectBar = styled(Box, { shouldForwardProp: (prop) => prop !== 'status' && prop !== 'remainingDays' && prop !== 'gridColumn' })(({ theme, status, remainingDays, gridColumn }) => ({
    gridColumn: gridColumn,
    display: 'flex',
    minWidth: 0,
    minHeight: theme.spacing(6),
    borderRadius: theme.shape.borderRadius*2,
    backgroundColor: getBackgroundColor(theme, remainingDays, status),
    boxShadow: theme.shadows[3],
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0, 1),
    color: theme.palette.primary.contrastText,
    cursor: 'pointer',
    "&:hover": {
        transform: 'scale(1.02)',
        transitionDuration: '0.2s',
        boxShadow: theme.shadows[8],
    },
}));

const ProjectBarName = styled(Typography)(({ theme }) => ({
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
}));

const ResponsibleAvatar = styled(Avatar, { shouldForwardProp: (prop) => prop !== 'remainingDays' && prop !== 'status' })(({ theme, remainingDays, status }) => ({
    width: theme.spacing(3),
    height: theme.spacing(3),
    marginLeft: theme.spacing(1),
    border: `1px solid white`,
    backgroundColor: getBackgroundColor(theme, remainingDays, status),
}));

const StyledBarTooltip = styled(({ className, ...props }) => <Tooltip {...props} classes={{ popper: className }} />)(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.background.paper,
        backdropFilter: 'blur(12px)',
        color: theme.palette.text.primary,
        border: `1px solid ${theme.palette.divider}`,
        fontSize: theme.typography.pxToRem(14),
        borderRadius: theme.shape.borderRadius*2,
        maxWidth: "480px",
        whiteSpace: "normal",
        wordBreak: "break-word",
        overflowWrap: "break-word",
        [theme.breakpoints.down('sm')]: {
            maxWidth: "320px",
            fontSize: theme.typography.pxToRem(12),
        },
    },
}));

const BarTooltip = ({ children, ...props }) => {
    const isTouch = useMediaQuery('(pointer: coarse)');
    if (isTouch) return children;
    return <StyledBarTooltip {...props}>{children}</StyledBarTooltip>;
};

const BarTooltipContentBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
}));

const BarTooltipContentName = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.typography.fontWeightMedium,
}));

const BarTooltipContentDateBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    gap: theme.spacing(1),
}));

const BarTooltipContentDate = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.pxToRem(12),
}));

const BarTooltipFooter = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
}));

const BarTooltipFooterItem = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.pxToRem(10),
}));

function getBackgroundColor(theme, remainingDays, status) {
    return remainingDays === 'Overdue' ? theme.palette.error.light : remainingDays === '0 Days' ? theme.palette.warning.light : {
        'Not Started': theme.palette.secondary.light,
        'In Progress': theme.palette.primary.light,
        'Pending': theme.palette.success.light,
        'Awaiting Authorization': theme.palette.warning.light,
    }[status] || theme.palette.surface.track;
}

/** Derive the remainingDays label used for bar coloring when the item doesn't carry one. */
function deriveRemainingDays(item) {
    if (item?.remainingDays) return item.remainingDays;
    if (!item?.dueDate) return undefined;
    const due = formatDate(item.dueDate);
    if (!due) return undefined;
    const diff = differenceInDays(due, toLocalDay());
    if (diff < 0) return 'Overdue';
    if (diff === 0) return '0 Days';
    return `${diff} Day${diff === 1 ? '' : 's'}`;
}

const BarTooltipContent = ({ project }) => {
    return (
        <BarTooltipContentBox>
            <BarTooltipContentName variant="body1">{project.name}</BarTooltipContentName>
            <BarTooltipContentDateBox>
                <ListItemText primary="Start Date" secondary={formatDayShort(project?.startDate) || 'N/A'} />
                <ListItemText primary="Due Date" secondary={formatDayShort(project?.dueDate) || 'N/A'} />
            </BarTooltipContentDateBox>
            <BarTooltipContentDate variant="body2">Timing: {project?.remainingDays} {project?.remainingDays === 'Overdue' ? '' : 'remaining'}</BarTooltipContentDate>
            <BarTooltipFooter>
                <BarTooltipFooterItem>Responsible: {project?.responsible?.fullName || 'N/A'}</BarTooltipFooterItem>
                <BarTooltipFooterItem>{project.status}</BarTooltipFooterItem>
            </BarTooltipFooter>
        </BarTooltipContentBox>
    );
};

const StickyHeaderWrapper = styled(Box, { shouldForwardProp: (prop) => prop !== 'sticky' })(({ theme, sticky }) => ({
    position: sticky ? 'sticky' : 'static',
    top: 0,
    zIndex: sticky ? 2 : 'auto',
    backgroundColor: sticky ? theme.palette.background.paper : 'transparent',
    paddingTop: sticky ? theme.spacing(0.125) : 0,
}));

const RangeTitle = styled(Typography)(({ theme }) => ({
    textAlign: 'center',
    flex: 1,
    fontSize: theme.typography.size.lg,
    [theme.breakpoints.down('sm')]: {
        fontSize: theme.typography.size.md,
    },
}));

const RangeEmptyMessage = styled(Typography)(({ theme }) => ({
    textAlign: 'center',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
}));

const ClickableProjectBar = styled(ProjectBar, { shouldForwardProp: (prop) => prop !== 'clickable' })(({ clickable }) => ({
    cursor: clickable ? 'pointer' : 'default',
}));

const AccountTabCalendar = ({ projects, getLink, emptyMessage = 'Nothing to show in this range', stickyHeader = false }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const daysToShow = isMobile ? 8 : 15;
    const [currentStartDate, setCurrentStartDate] = useState(addDays(new Date(), -7));
    const navigate = useNavigate();

    const defaultGetLink = (project) => {
        const slug = project?.account?.slug || project?.account?.name;
        return slug ? `/account/${slug}/project/${project._id}` : null;
    };
    const resolveLink = typeof getLink === 'function' ? getLink : defaultGetLink;

    const generateDates = (startDate) => {
        const dates = [];
        for (let i = 0; i < daysToShow; i++) {
            dates.push(addDays(startDate, i));
        }
        return dates;
    };

    const dates = generateDates(currentStartDate);

    const navigateDays = (direction) => {
        const daysToNavigate = isMobile ? 8 : 7;
        setCurrentStartDate((prevDate) => addDays(prevDate, direction === 'prev' ? -daysToNavigate : daysToNavigate));
    };

    const getGridColumnForProject = (project) => {
        if (!project.startDate || !project.dueDate) {
            return { gridColumn: '1 / 2', display: false };
        }
        const startDay = formatDate(project.startDate);
        const endDay = formatDate(project.dueDate);
        const windowStart = toLocalDay(currentStartDate);
        if (!startDay || !endDay || !windowStart) {
            return { gridColumn: '1 / 2', display: false };
        }

        let start = differenceInDays(startDay, windowStart) + 1;
        let end = differenceInDays(endDay, windowStart) + 1;
        // Inverted or accidentally-equal dates collapse to a single-day bar.
        if (end < start) end = start;

        let finalStart = Math.max(1, start);
        let finalEnd = Math.min(daysToShow, end);

        const display = !(finalStart > daysToShow || finalEnd < 1 || finalEnd < finalStart);

        return {
            gridColumn: `${finalStart} / ${finalEnd + 1}`,
            display,
        };
    };

    const visibleProjects = (projects || []).filter((p) => getGridColumnForProject(p).display);

    return (
        <Container>
            <StickyHeaderWrapper sticky={stickyHeader}>
                <Header>
                    <WeekButton onClick={() => navigateDays("prev")}>
                        <ChevronLeft className="h-4 w-4" />
                        <ButtonText variant="body2">Previous Week</ButtonText>
                    </WeekButton>
                    <RangeTitle variant="h6">
                        {formatDayLong(toLocalDay(dates[0])).split(",")[0]} - {formatDayLong(toLocalDay(dates[dates.length - 1]))}
                    </RangeTitle>
                    <WeekButton onClick={() => navigateDays("next")}>
                        <ButtonText variant="body2">Next Week</ButtonText>
                        <ChevronRight className="h-4 w-4" />
                    </WeekButton>
                </Header>

                {/* Dates Grid */}
                <DatesGrid>
                    {dates.map((date) => (
                        <DateBox aria-label={date.getDate()} key={date.getDate()} isToday={toLocalDay(date) === toLocalDay()}>
                            <DateText variant="caption">{WEEKDAY_LABELS[date.getDay()]}</DateText>
                            <DateDay variant="body1">
                                {date.getDate()}
                            </DateDay>
                        </DateBox>
                    ))}
                </DatesGrid>
            </StickyHeaderWrapper>

            {/* Project Bars Grid */}
            {visibleProjects.length === 0 ? (
                <RangeEmptyMessage variant="body2" color="text.secondary">
                    {emptyMessage}
                </RangeEmptyMessage>
            ) : (
                <ProjectBarsGrid>
                    {(projects || []).map((project, index) => {
                        const { gridColumn, display } = getGridColumnForProject(project);
                        if (!display) return null;
                        const link = resolveLink(project);
                        const initial = project.responsible?.fullName ? project.responsible.fullName.charAt(0) : '';
                        const key = `${project._id || project.id || 'item'}-${index}`;
                        const remainingDays = deriveRemainingDays(project);
                        const projectForTooltip = project.remainingDays ? project : { ...project, remainingDays };
                        return (
                            <Fragment key={key}>
                                <BarTooltip title={<BarTooltipContent project={projectForTooltip} />} arrow>
                                    <ClickableProjectBar
                                        status={project.status}
                                        remainingDays={remainingDays}
                                        gridColumn={gridColumn}
                                        clickable={Boolean(link)}
                                        onClick={() => link && navigate(link)}
                                    >
                                        <ProjectBarName variant="body2">{project.name}</ProjectBarName>
                                        {project.responsible && (
                                            <ResponsibleAvatar
                                                title={project.responsible?.fullName}
                                                src={getAvatarUrl(project.responsible)}
                                                remainingDays={remainingDays}
                                                status={project.status}
                                            >
                                                {initial}
                                            </ResponsibleAvatar>
                                        )}
                                    </ClickableProjectBar>
                                </BarTooltip>
                            </Fragment>
                        );
                    })}
                </ProjectBarsGrid>
            )}
        </Container>
    );
};

export default AccountTabCalendar;
